import { UseQueryResult, useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import {
  ChatFunctionCall,
  Function_,
  JSONSchema,
  Tool,
  ToolChoice,
} from "@/types/prompt-blueprint";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

export type RequestInputVariables = Record<string, string | boolean | object>;

export interface RequestToolData {
  tools?: Tool[] | null;
  tool_choice?: ToolChoice | null;
  functions?: Function_[] | null;
  function_call?: ChatFunctionCall | null;
  json_schema?: { json_schema: JSONSchema; type: "json_schema" } | null;
}

export interface RequestLogInputVariablesResponse {
  success: boolean;
  prompt_input_variables: RequestInputVariables;
  request_tool_data?: RequestToolData | null;
}

export const fetchRequestLogInputVariables = async (
  userToken: string | null | undefined,
  requestLogId: number,
): Promise<RequestLogInputVariablesResponse> => {
  if (!requestLogId) {
    throw new Error("Request log ID is required");
  }
  if (!userToken) {
    throw new Error("User token is required");
  }

  const response = await fetch(ENDPOINTS.request_log_input_variables(requestLogId), {
    headers: authHeader(userToken),
  });

  return await handleResponse(response);
};

export const useRequestLogInputVariables = (
  userToken: string | null | undefined,
  requestLogId: number | null,
): UseQueryResult<RequestLogInputVariablesResponse> =>
  useQuery({
    queryKey: ["requestLogInputVariables", requestLogId, userToken],
    queryFn: () => fetchRequestLogInputVariables(userToken, requestLogId ?? 0),
    enabled: !!requestLogId && !!userToken,
    retry: false,
    staleTime: Infinity,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
